import React, { useEffect, useRef, useState } from "react";
import { api, fmtError, fmtMoney, fmtNumber } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { TrendUp, TrendDown, Envelope, Eye, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const RECIPIENT_OPTIONS = [
    { value: "self", label: "Just me", roles: "*" },
    { value: "client_servicing", label: "All Client Servicing", roles: ["super_admin", "execution_head"] },
    { value: "execution_head", label: "All Execution Heads", roles: ["super_admin"] },
    { value: "accounts", label: "All Accounts", roles: ["super_admin", "execution_head"] },
    { value: "all_team", label: "Everyone on the team", roles: ["super_admin"] },
];

export default function Digest() {
    const { user } = useAuth();
    const [digest, setDigest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [recipient, setRecipient] = useState("self");
    const [lastResult, setLastResult] = useState(null);
    const previewIframeRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/digest/weekly");
            setDigest(data);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        // Load preview HTML into iframe
        if (!digest || !previewIframeRef.current) return;
        (async () => {
            try {
                const res = await api.get("/digest/preview-html", { responseType: "text" });
                const doc = previewIframeRef.current.contentDocument;
                if (doc) {
                    doc.open();
                    doc.write(res.data);
                    doc.close();
                }
            } catch (e) {
                // silent
            }
        })();
    }, [digest]);

    const send = async () => {
        setBusy(true);
        setLastResult(null);
        try {
            const { data } = await api.post("/digest/send", { recipients: recipient });
            setLastResult(data);
            const stubbed = data.results?.[0]?.stubbed;
            toast.success(
                stubbed
                    ? `Digest queued for ${data.sent_count} recipient(s) — email is stubbed (no Resend key)`
                    : `Digest sent to ${data.sent_count} recipient(s)`,
            );
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const recipientChoices = RECIPIENT_OPTIONS.filter(
        (o) => o.roles === "*" || (user?.role === "super_admin") || o.roles.includes(user?.role),
    );

    if (loading) return <div className="font-mono text-xs text-slate-400">loading digest…</div>;
    if (!digest) return <div className="text-sm text-slate-500">No digest available.</div>;

    const s = digest.pace_summary || {};

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="overline mb-1">// Monday morning</div>
                    <h1 className="font-display text-4xl tracking-tighter">Weekly Digest</h1>
                    <p className="text-slate-500 mt-2 text-sm">
                        A snapshot of last week — pace, movers, approvals, and revenue heads-up. Preview below and send to your team.
                    </p>
                </div>
                <div className="flex items-center gap-2" data-testid="digest-send-controls">
                    <Select value={recipient} onValueChange={setRecipient}>
                        <SelectTrigger
                            data-testid="digest-recipient-select"
                            className="rounded-none border-slate-300 w-56 h-10"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {recipientChoices.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        onClick={send}
                        disabled={busy}
                        data-testid="digest-send-button"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2 px-5"
                    >
                        <PaperPlaneTilt size={14} weight="bold" />
                        {busy ? "Sending…" : "Send digest"}
                    </Button>
                </div>
            </header>

            {/* Data overview */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                <Stat label="Active campaigns" value={fmtNumber(s.total_active || 0)} sub={`${s.on_track || 0} on track`} testid="digest-stat-active" />
                <Stat label="Pending approvals" value={fmtNumber(digest.pending_approvals || 0)} sub="awaiting review" testid="digest-stat-pending" />
                <Stat label="New campaigns" value={fmtNumber(digest.new_campaigns_count || 0)} sub="last 7 days" testid="digest-stat-new" />
                <Stat label="Revenue forecast" value={fmtMoney(digest.total_revenue_forecast || 0)} sub={`${digest.upcoming_invoiceable?.count || 0} ending in 7d`} testid="digest-stat-revenue" />
            </section>

            {/* Movers */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                <MoverCard
                    title="Top gainers"
                    items={digest.top_gainers || []}
                    color="#15803D"
                    bg="#DCFCE7"
                    Icon={TrendUp}
                    testid="digest-gainers"
                />
                <MoverCard
                    title="Watch list — declines"
                    items={digest.top_decliners || []}
                    color="#B91C1C"
                    bg="#FEE2E2"
                    Icon={TrendDown}
                    testid="digest-decliners"
                />
            </section>

            {/* Preview iframe */}
            <section className="bg-white border border-slate-200" data-testid="digest-preview">
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
                    <div className="flex items-center gap-2.5">
                        <Eye size={16} className="text-slate-500" />
                        <div>
                            <div className="overline">Preview</div>
                            <h3 className="font-display text-base">Email as recipient will see it</h3>
                        </div>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                        Subject · Pulse — Your weekly digest
                    </span>
                </div>
                <div className="bg-slate-100 p-5">
                    <iframe
                        ref={previewIframeRef}
                        title="digest-preview"
                        data-testid="digest-iframe"
                        className="w-full h-[720px] bg-white border border-slate-300"
                        sandbox="allow-same-origin"
                    />
                </div>
            </section>

            {lastResult && (
                <section className="bg-white border border-slate-200 p-5" data-testid="digest-send-result">
                    <div className="overline mb-2">Last send result</div>
                    <div className="flex items-center gap-2 text-sm">
                        <Envelope size={16} className="text-slate-500" />
                        <span>
                            <b>{lastResult.sent_count}</b> email(s) processed
                            {lastResult.results?.[0]?.stubbed && (
                                <span className="ml-2 font-mono text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5">
                                    stub — set RESEND_API_KEY to send for real
                                </span>
                            )}
                        </span>
                    </div>
                    <ul className="mt-3 divide-y divide-slate-100">
                        {lastResult.results?.map((r, i) => (
                            <li key={i} className="py-2 flex items-center justify-between" data-testid={`digest-send-row-${i}`}>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium">{r.name || r.email}</div>
                                    <div className="text-[10px] font-mono text-slate-500">{r.email}</div>
                                </div>
                                <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 ${r.stubbed ? "bg-amber-100 text-amber-800" : r.ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                    {r.stubbed ? "stubbed" : r.ok ? "sent" : "failed"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}

function Stat({ label, value, sub, testid }) {
    return (
        <div className="bg-white p-5 animate-fade-up" data-testid={testid}>
            <div className="overline">{label}</div>
            <div className="font-mono text-2xl md:text-3xl mt-1 tracking-tighter">{value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
        </div>
    );
}

function MoverCard({ title, items, color, bg, Icon, testid }) {
    return (
        <div className="bg-white p-5" data-testid={testid}>
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="overline">WoW</div>
                    <h3 className="font-display text-lg">{title}</h3>
                </div>
                <span style={{ backgroundColor: bg, color }} className="w-8 h-8 inline-flex items-center justify-center">
                    <Icon size={16} weight="bold" />
                </span>
            </div>
            {items.length === 0 ? (
                <p className="text-sm text-slate-500">No movement recorded.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {items.map((m, i) => (
                        <li key={m.id} className="py-3 flex items-center justify-between gap-3" data-testid={`${testid}-row-${i}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{m.name}</div>
                                    <div className="overline text-slate-400 truncate" style={{ fontSize: 9 }}>{m.client_org_name}</div>
                                </div>
                            </div>
                            <span className="font-mono text-base font-semibold whitespace-nowrap" style={{ color }}>
                                {m.wow_percent >= 0 ? "+" : ""}
                                {m.wow_percent.toFixed(0)}%
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
